import { useContext, useMemo, useState } from "react";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Divider,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	FormControlLabel,
	Checkbox,
	MenuItem,
	Paper,
	Stack,
	Tab,
	Tabs,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	TextField,
	Typography
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Delete from "@mui/icons-material/Delete";
import { buildMessageBrandOptions } from "./targeting";
import { Link, Navigate, Outlet, useNavigate, useParams } from "react-router";
import { useGetUserDetailsQuery } from "features/api/userApi";
import ConfigurationsContext from "context/configurations/ConfigurationsContext";
import MarketingMaterialsBrandProductLineFilter from "features/MarketingMaterials/pages/components/MarketingMaterialsBrandProductLineFilter/MarketingMaterialsBrandProductLineFilter";
import {
	useDeleteMessageMutation,
	useGetMessageOptionsQuery,
	useGetMessageQuery,
	useGetMessagesQuery,
	useSaveMessageMutation
} from "./messagesApi";
import { canManageMessages, messageError, pageLabels, type Message, type MessageOptions } from "./types";
import { easternInputToUtc, formatZoned } from "./time";
import { EasternDateTimeField } from "./EasternDateTimeField";
import { MessagePreview } from "./MessagePreview";

export function MessagesAccess() {
	const { data: user, isLoading } = useGetUserDetailsQuery(undefined, {
		refetchOnMountOrArgChange: true,
		pollingInterval: 60000
	});
	const { configurationsData } = useContext(ConfigurationsContext);
	if (isLoading || !configurationsData) return <CircularProgress aria-label="Loading permissions" />;
	if (!configurationsData.featureFlags?.bannersAndAdsEnabled || !canManageMessages(user?.permissions ?? []))
		return (
			<Navigate
				to="/not-found"
				replace
			/>
		);
	return <Outlet />;
}
function Heading({ title }: { title: string }) {
	return (
		<Box sx={{ py: 3, px: { xs: 2, md: 4 }, background: "linear-gradient(100deg, #006c90, #fff)" }}>
			<Typography
				component="h1"
				variant="h3"
				color="white"
			>
				{title}
			</Typography>
		</Box>
	);
}
export function MessagesListPage() {
	const [status, setStatus] = useState<"current" | "expired">("current");
	const [descending, setDescending] = useState(true);
	const {
		data = [],
		error,
		isLoading
	} = useGetMessagesQuery(status, { pollingInterval: 60000, refetchOnFocus: true });
	const rows = [...data].sort((a, b) => (Date.parse(b.startUtc) - Date.parse(a.startUtc)) * (descending ? 1 : -1));
	return (
		<>
			<Heading title="Banners & Ads" />
			<Paper sx={{ m: { xs: 2, md: 3 }, p: { xs: 2, md: 3 } }}>
				<Stack
					direction={{ xs: "column", sm: "row" }}
					gap={3}
					alignItems={{ sm: "center" }}
					mb={2}
				>
					<Tabs
						value={status}
						onChange={(_, value) => setStatus(value)}
						aria-label="Message status"
					>
						<Tab
							label="Current Messages"
							value="current"
						/>
						<Tab
							label="Expired Messages"
							value="expired"
						/>
					</Tabs>
					<Button
						component={Link}
						to="create"
						variant="contained"
						startIcon={<Add />}
					>
						Create Message
					</Button>
				</Stack>
				{error && <Alert severity="error">Unable to load messages. Please try again.</Alert>}
				{isLoading ? (
					<CircularProgress aria-label="Loading messages" />
				) : (
					<TableContainer>
						<Table aria-label="Messages">
							<TableHead>
								<TableRow>
									<TableCell>
										<TableSortLabel
											active
											direction={descending ? "desc" : "asc"}
											onClick={() => setDescending(!descending)}
										>
											Start Date (ET)
										</TableSortLabel>
									</TableCell>
									{["End Date (ET)", "Name", "Type", "Page", "Link", ""].map((label) => (
										<TableCell key={label}>{label}</TableCell>
									))}
								</TableRow>
							</TableHead>
							<TableBody>
								{rows.map((row) => (
									<TableRow key={row.messageId}>
										<TableCell>{formatZoned(row.startUtc)}</TableCell>
										<TableCell>{formatZoned(row.endUtc)}</TableCell>
										<TableCell>{row.name}</TableCell>
										<TableCell>{row.type === "Banner" ? "Text Banner" : "Outage Banner"}</TableCell>
										<TableCell>{pageLabels[row.page]}</TableCell>
										<TableCell sx={{ maxWidth: 250, overflowWrap: "anywhere" }}>
											{row.linkUrl && (
												<a
													href={row.linkUrl}
													target="_blank"
													rel="noopener noreferrer"
												>
													{row.linkUrl}
												</a>
											)}
										</TableCell>
										<TableCell>
											<Button
												component={Link}
												to={encodeURIComponent(row.messageId)}
												aria-label={"Edit " + row.name}
											>
												<ChevronRight />
											</Button>
										</TableCell>
									</TableRow>
								))}
								{!rows.length && (
									<TableRow>
										<TableCell colSpan={7}>No {status} messages.</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Paper>
		</>
	);
}
const emptyMessage: Message = {
	messageId: "",
	name: "",
	type: "Banner",
	startUtc: "",
	endUtc: "",
	channels: [],
	brands: [],
	text: "",
	page: "Home",
	showOnPromotionsPage: false,
	erps: [],
	disablePlacingNewOrders: false
};
export function MessageEditorPage() {
	const { messageId } = useParams();
	const {
		data,
		error: loadError,
		isLoading,
		isFetching
	} = useGetMessageQuery(messageId ?? "", { skip: !messageId, refetchOnMountOrArgChange: true });
	const {
		data: options,
		error: optionsError,
		isLoading: loadingOptions,
		isFetching: fetchingOptions,
		refetch: retryOptions
	} = useGetMessageOptionsQuery();
	if (messageId && loadError) return <Alert severity="error">Unable to load this message.</Alert>;
	if (optionsError || (!options && !loadingOptions && !fetchingOptions))
		return (
			<Alert
				severity="error"
				action={
					<Button
						color="inherit"
						onClick={() => void retryOptions()}
					>
						Retry
					</Button>
				}
			>
				Unable to load message options. Please try again.
			</Alert>
		);
	if ((messageId && (isLoading || isFetching)) || !options) return <CircularProgress aria-label="Loading message" />;
	return (
		<MessageForm
			key={messageId ?? "create"}
			initial={messageId && data ? data : emptyMessage}
			options={options}
		/>
	);
}
function normalizeMulti(values: string[], current: string[]) {
	if (values.at(-1) === "ALL" && current.length) return [];
	return values.filter((value) => value !== "ALL");
}
function MessageForm({ initial, options }: { initial: Message; options: MessageOptions }) {
	const navigate = useNavigate();
	const { data: user } = useGetUserDetailsQuery();
	const allowedTypes = options.types ?? [
		...(user?.permissions?.some((permission) => String(permission) === "CONTENT_MANAGER")
			? ["Banner" as const]
			: []),
		...(user?.permissions?.some((permission) => String(permission) === "OUTAGE_MANAGER") ? ["Outage" as const] : [])
	];
	const [form, setForm] = useState(() =>
		initial.messageId ? initial : { ...initial, type: allowedTypes[0] ?? "Banner" }
	);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [start, setStart] = useState(initial.startUtc ? formatZoned(initial.startUtc) : "");
	const [end, setEnd] = useState(initial.endUtc ? formatZoned(initial.endUtc) : "");
	const [error, setError] = useState("");
	const [save, saveState] = useSaveMessageMutation();
	const [remove, deleteState] = useDeleteMessageMutation();
	const busy = saveState.isLoading || deleteState.isLoading;
	const brandOptions = useMemo(() => buildMessageBrandOptions(options.productLines), [options.productLines]);
	const selectedIds = form.brands.flatMap((selection) =>
		selection.allProductLine
			? (brandOptions.find((brand) => brand.id === selection.brandCode)?.productLines.map((line) => line.id) ??
				[])
			: selection.productLineKeys
	);
	const selectionLabels = form.brands.map((selection) => {
		const brand = brandOptions.find((candidate) => candidate.id === selection.brandCode);
		return (
			(brand?.name ?? selection.brandCode) +
			(selection.allProductLine ? " (All)" : " (" + selection.productLineKeys.length + ")")
		);
	});
	const changeBrands = (ids: string[]) =>
		setForm({
			...form,
			brands: brandOptions.flatMap((brand) => {
				const selected = brand.productLines.filter((line) => ids.includes(line.id));
				return selected.length
					? [
							{
								brandCode: brand.id,
								allProductLine: selected.length === brand.productLines.length,
								productLineKeys:
									selected.length === brand.productLines.length ? [] : selected.map((line) => line.id)
							}
						]
					: [];
			})
		});
	const submit = async (event: React.FormEvent) => {
		event.preventDefault();
		setError("");
		try {
			const startUtc = easternInputToUtc(start),
				endUtc = easternInputToUtc(end);
			if (Date.parse(endUtc) <= Date.parse(startUtc)) throw new Error("End date must be after start date.");
			if (form.linkUrl && !/^https?:\/\//i.test(form.linkUrl))
				throw new Error("Link URL must use HTTP or HTTPS.");
			await save({
				...form,
				startUtc,
				endUtc,
				page: form.type === "Outage" ? "Everywhere" : form.page,
				erps: form.type === "Outage" ? form.erps : [],
				disablePlacingNewOrders: form.type === "Outage" && form.disablePlacingNewOrders,
				showOnPromotionsPage: form.type === "Banner" && form.showOnPromotionsPage
			}).unwrap();
			navigate("/banners-ads");
		} catch (failure) {
			setError(failure instanceof Error ? failure.message : messageError(failure));
		}
	};
	const deleteMessage = async () => {
		try {
			if (form.messageId) await remove(form.messageId).unwrap();
			navigate("/banners-ads");
		} catch (failure) {
			setError(messageError(failure));
		}
	};
	return (
		<>
			<Heading title={form.messageId ? "Edit Message" : "Create Message"} />
			<Paper
				component="form"
				onSubmit={submit}
				sx={{ m: { xs: 2, md: 3 }, p: { xs: 2, md: 3 } }}
			>
				<Stack gap={3}>
					<Typography
						component="h2"
						variant="h6"
					>
						{form.messageId ? "EDIT MESSAGE" : "CREATE MESSAGE"}
					</Typography>
					{error && <Alert severity="error">{error}</Alert>}
					<TextField
						label="Message Name"
						required
						value={form.name}
						onChange={(event) => setForm({ ...form, name: event.target.value })}
						sx={{ maxWidth: 610 }}
					/>
					<EasternDateTimeField
						label="Start Date & Time (Eastern)"
						value={start}
						onChange={setStart}
					/>
					<EasternDateTimeField
						label="End Date & Time (Eastern)"
						value={end}
						onChange={setEnd}
					/>
					<Autocomplete
						multiple
						options={["ALL", ...options.channels]}
						value={form.channels.length ? form.channels : ["ALL"]}
						getOptionLabel={(value) => (value === "ALL" ? "All Channels" : value.replaceAll("_", " "))}
						onChange={(_, values) => setForm({ ...form, channels: normalizeMulti(values, form.channels) })}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Channel"
							/>
						)}
						sx={{ maxWidth: 610 }}
					/>
					<Box sx={{ maxWidth: 610 }}>
						<MarketingMaterialsBrandProductLineFilter
							renderAsField
							selectionLabels={selectionLabels}
							onRemoveSelection={(index) =>
								setForm({ ...form, brands: form.brands.filter((_, i) => i !== index) })
							}
							selectedItems={selectedIds}
							onChange={changeBrands}
							brandAndProductLine={brandOptions}
						/>
					</Box>
					<TextField
						label="Link URL (optional)"
						value={form.linkUrl ?? ""}
						onChange={(event) => setForm({ ...form, linkUrl: event.target.value })}
						sx={{ maxWidth: 610 }}
					/>
					<TextField
						select
						label="Type"
						required
						value={form.type}
						onChange={(event) => setForm({ ...form, type: event.target.value as Message["type"] })}
						sx={{ maxWidth: 610 }}
					>
						{allowedTypes.map((type) => (
							<MenuItem
								key={type}
								value={type}
							>
								{type === "Banner" ? "Text Banner" : "Outage Banner"}
							</MenuItem>
						))}
					</TextField>
					<Divider />
					<Typography
						component="h2"
						variant="h6"
					>
						{form.type === "Outage" ? "OUTAGE BANNER MESSAGE" : "TEXT BANNER MESSAGE"}
					</Typography>
					{form.type === "Outage" ? (
						<>
							<Autocomplete
								multiple
								options={["ALL", ...options.erps]}
								value={form.erps.length ? form.erps : ["ALL"]}
								getOptionLabel={(value) => (value === "ALL" ? "All ERPs" : value)}
								onChange={(_, values) => setForm({ ...form, erps: normalizeMulti(values, form.erps) })}
								renderInput={(params) => (
									<TextField
										{...params}
										label="ERP"
									/>
								)}
								sx={{ maxWidth: 610 }}
							/>
							<FormControlLabel
								label="Disable Placing New Orders"
								control={
									<Checkbox
										checked={form.disablePlacingNewOrders}
										onChange={(_, checked) =>
											setForm({ ...form, disablePlacingNewOrders: checked })
										}
									/>
								}
							/>
						</>
					) : (
						<>
							<TextField
								select
								label="Page"
								required
								value={form.page}
								onChange={(event) => setForm({ ...form, page: event.target.value as Message["page"] })}
								sx={{ maxWidth: 610 }}
							>
								{options.pages.map((page) => (
									<MenuItem
										key={page}
										value={page}
									>
										{pageLabels[page]}
									</MenuItem>
								))}
							</TextField>
							<FormControlLabel
								label="Show on Promotions page"
								control={
									<Checkbox
										checked={form.showOnPromotionsPage}
										onChange={(_, checked) => setForm({ ...form, showOnPromotionsPage: checked })}
									/>
								}
							/>
						</>
					)}
					<TextField
						label="Message Text"
						required
						multiline
						minRows={2}
						value={form.text}
						slotProps={{ htmlInput: { maxLength: 1000 } }}
						helperText={form.text.length + "/1000"}
						onChange={(event) => setForm({ ...form, text: event.target.value })}
					/>
					<Typography
						component="h2"
						variant="h6"
					>
						MESSAGE PREVIEW
					</Typography>
					<MessagePreview message={form} />
					<Stack
						direction={{ xs: "column", sm: "row" }}
						gap={2}
					>
						<Button
							color="error"
							sx={{ color: "#d32f2f", borderColor: "#d32f2f" }}
							variant="outlined"
							startIcon={<Delete />}
							disabled={busy}
							onClick={() => (form.messageId ? setConfirmDelete(true) : void deleteMessage())}
						>
							Delete Message
						</Button>
						<Box sx={{ flexGrow: 1 }} />
						<Button
							component={Link}
							to="/banners-ads"
							variant="outlined"
							disabled={busy}
						>
							Cancel
						</Button>
						<Button
							type="submit"
							variant="contained"
							disabled={busy}
						>
							Save
						</Button>
					</Stack>
				</Stack>
			</Paper>
			<Dialog
				open={confirmDelete}
				onClose={() => !busy && setConfirmDelete(false)}
			>
				<DialogTitle>Delete message?</DialogTitle>
				<DialogContent>This will permanently delete {form.name}.</DialogContent>
				<DialogActions>
					<Button
						disabled={busy}
						onClick={() => setConfirmDelete(false)}
					>
						Cancel
					</Button>
					<Button
						color="error"
						disabled={busy}
						onClick={() => void deleteMessage()}
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
